import {
  clearPwaData,
  getMeta,
  openPwaDb,
  putSnapshot,
  setMeta,
} from "./storage";

const contextKeys = [
  "actor_context_hash",
  "permission_version",
  "pwa_schema_version",
  "actor_type",
  "company_scope",
];

const sameContext = (previous, current) =>
  previous &&
  contextKeys.every((key) => String(previous[key] ?? "") === String(current[key] ?? ""));

const notifyWorkerPurge = async () => {
  if (!("serviceWorker" in navigator)) return;
  const registration = await navigator.serviceWorker.getRegistration();
  const worker = registration?.active || registration?.waiting || registration?.installing;
  worker?.postMessage({ type: "PWA_PURGE_PRIVATE" });
};

const disablePwaWorker = async (appType) => {
  if ("serviceWorker" in navigator) {
    const registrations = await navigator.serviceWorker.getRegistrations();
    await Promise.all(registrations
      .filter((registration) => {
        const scopePath = new URL(registration.scope).pathname;
        return appType === "admin" ? scopePath.startsWith("/admin/") : scopePath === "/";
      })
      .map((registration) => registration.unregister()));
  }

  if ("caches" in window) {
    const prefix = appType === "admin" ? "ceoweb-admin-shell-" : "ceoweb-user-shell-";
    const names = await caches.keys();
    await Promise.all(names.filter((name) => name.startsWith(prefix)).map((name) => caches.delete(name)));
  }
};

const broadcast = (message) => {
  const payload = {
    ...message,
    source: window.__CEOWEB_PWA_INSTANCE__ || "",
  };
  if ("BroadcastChannel" in window) {
    const channel = new BroadcastChannel("ceoweb-pwa");
    channel.postMessage(payload);
    channel.close();
    return;
  }
  try {
    window.localStorage.setItem("ceoweb_pwa_broadcast", JSON.stringify({
      ...payload,
      at: Date.now(),
    }));
  } catch (error) {
    // Cross-tab synchronization remains an optional enhancement.
  }
};

export const telemetry = (config, event, detail = {}) => {
  if (!config.telemetryUrl) return;
  const body = JSON.stringify({ event, detail });
  fetch(config.telemetryUrl, {
    method: "POST",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    body,
    keepalive: true,
  }).catch(() => {});
};

const syncResource = async (config, db, context, resource) => {
  const startedAt = performance.now();
  const url = new URL(config.snapshotUrl, window.location.origin);
  url.searchParams.set("resource", resource.code);

  const response = await fetch(url.toString(), {
    credentials: "same-origin",
    cache: "no-store",
    headers: { Accept: "application/json" },
  });
  if (!response.ok) {
    throw new Error(`snapshot_http_${response.status}`);
  }

  const body = await response.json();
  const snapshot = body?.data;
  if (!body?.ok || !snapshot || snapshot.resource !== resource.code) {
    throw new Error("snapshot_invalid");
  }

  const record = {
    key: `${snapshot.resource}:default`,
    resource: snapshot.resource,
    label: snapshot.label || resource.label || snapshot.resource,
    actorContextHash: context.actor_context_hash,
    actorType: context.actor_type,
    companyScope: context.company_scope,
    permissionVersion: context.permission_version,
    schemaVersion: context.pwa_schema_version,
    dataVersion: snapshot.data_version,
    updatedAt: snapshot.updated_at,
    expiresAt: snapshot.expires_at,
    sensitivity: snapshot.sensitivity,
    payload: snapshot.payload || {},
  };

  await putSnapshot(db, record);
  telemetry(config, "snapshot_sync_success", {
    resource: resource.code,
    duration_ms: Math.round(performance.now() - startedAt),
    size: JSON.stringify(record).length,
  });
};

export const bootstrapAndSync = async (config) => {
  if (!config.bootstrapUrl) return null;

  const response = await fetch(config.bootstrapUrl, {
    credentials: "same-origin",
    cache: "no-store",
    headers: { Accept: "application/json" },
  });

  if (response.status === 401 || response.redirected) {
    await clearPwaData(config.appType);
    broadcast({ type: "context-wiped", reason: "reauth-required" });
    window.dispatchEvent(new CustomEvent("ceoweb:pwa:reauth-required"));
    return null;
  }
  if (!response.ok) {
    throw new Error(`bootstrap_http_${response.status}`);
  }

  const body = await response.json();
  const context = body?.data;
  if (!body?.ok || !context?.actor_context_hash) {
    throw new Error("bootstrap_invalid");
  }

  let db = await openPwaDb(config.appType);
  const previous = await getMeta(db, "security_context");
  if (previous && !sameContext(previous, context)) {
    db.close();
    await clearPwaData(config.appType);
    await notifyWorkerPurge();
    broadcast({ type: "context-wiped", reason: "security-context-changed" });
    db = await openPwaDb(config.appType);
  }

  if (context.flags?.kill_switch || context.is_impersonating) {
    db.close();
    await clearPwaData(config.appType);
    await notifyWorkerPurge();
    if (context.flags?.kill_switch) {
      await disablePwaWorker(config.appType);
    }
    broadcast({
      type: "context-wiped",
      reason: context.flags?.kill_switch ? "kill-switch" : "impersonation",
    });
    return context;
  }

  await setMeta(db, "security_context", context);
  await setMeta(db, "build_id", context.build_id);

  const resources = Array.isArray(context.resources) ? context.resources : [];
  if (context.flags?.offline_snapshots && resources.length) {
    window.dispatchEvent(new CustomEvent("ceoweb:pwa:syncing"));
    for (const resource of resources) {
      try {
        await syncResource(config, db, context, resource);
      } catch (error) {
        telemetry(config, "snapshot_sync_failed", {
          resource: resource.code || "unknown",
          reason: String(error?.message || "sync_failed").slice(0, 100),
        });
      }
    }
    const syncedAt = new Date().toISOString();
    await setMeta(db, "last_sync_at", syncedAt);
    window.dispatchEvent(new CustomEvent("ceoweb:pwa:synced", { detail: { syncedAt } }));
  }

  db.close();
  return context;
};
